import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  Package,
  DollarSign,
  Ruler,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { Alert, AlertDescription } from "@shared/ui/alert";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useMaterials,
  useCreateMaterial,
  useUpdateMaterial,
  useToggleMaterialStatus,
  useOperationTypes,
} from "../../../../hooks";
import { Material } from "../../../../types";
import { useCurrency } from "@shared/selectors/globalSelectors";

// ====================== TYPES ======================

interface MaterialFormData {
  name: string;
  operationType: string;
  size: string;
  quantity: string;
  costPerUnit: string;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// Material card component for better reusability
const MaterialCard: React.FC<{
  material: Material;
  onEdit: (material: Material) => void;
  onToggleActive: (material: Material) => void;
  isUpdating: boolean;
  currentMaterial: Material | null;
  isToggling: boolean;
}> = ({
  material,
  onEdit,
  onToggleActive,
  isUpdating,
  currentMaterial,
  isToggling,
}) => {
  const currency = useCurrency();
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2">
              <Package className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium">{material.name}</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Type: {material.workOrderType}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(material)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
              disabled={isUpdating}
            >
              {isUpdating && currentMaterial?.id === material.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Ruler className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-muted-foreground">Size:</span>
              <span className="font-medium">{material.size}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Package className="h-3.5 w-3.5 text-green-600" />
              <span className="text-muted-foreground">Quantity:</span>
              <span className="font-medium">{material.quantity}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="h-3.5 w-3.5 text-orange-600" />
              <span className="text-muted-foreground">Cost/Unit:</span>
              <span className="font-medium">
                {currency} {material.costPerUnit.toFixed(2)}
              </span>
            </div>

            <div className="pt-2 border-t">
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="h-3.5 w-3.5 text-purple-600" />
                <span className="text-muted-foreground">Total Cost:</span>
                <span className="font-medium text-purple-600">
                  {currency} {(material.quantity * material.costPerUnit).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t">
            <div className="flex gap-2 items-center text-sm text-muted-foreground mb-2">
              <User className="h-3.5 w-3.5" />
              <p>{material.createdBy}</p>
            </div>
            <div className="flex gap-2 items-center text-sm text-muted-foreground mb-4">
              <Calendar className="h-3.5 w-3.5" />
              <p>{material.createdDate}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <span className="text-sm">
              {material.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={material.isActive}
              onCheckedChange={() => onToggleActive(material)}
              disabled={isToggling}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
  isLoadingOperationTypes: boolean;
}> = ({ hasSearchQuery, onAddNew, isLoadingOperationTypes }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <h4>No materials found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new material"
            : "No materials available"}
        </p>
        <Button
          variant="outline"
          className="mt-2"
          onClick={onAddNew}
          disabled={isLoadingOperationTypes}
        >
          <Plus className="mr-2 h-4 w-4" /> Add New Material
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const MaterialsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentMaterial, setCurrentMaterial] = useState<Material | null>(null);

  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: materials = [] } = useMaterials(searchQuery);
  const { data: operationTypes = [], isLoading: loadingOperationTypes } =
    useOperationTypes();
  const createMaterialMutation = useCreateMaterial();
  const updateMaterialMutation = useUpdateMaterial();
  const toggleStatusMutation = useToggleMaterialStatus();

  // Transform operation types for form options
  const operationTypeOptions = useMemo(
    () =>
      operationTypes.map((type) => ({
        value: type.key.toString(),
        label: type.value,
      })),
    [operationTypes]
  );

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "operationType",
        type: "select",
        label: "Operation Type",
        placeholder: loadingOperationTypes
          ? "Loading operation types..."
          : "Select operation type...",
        required: true,
        className: "col-span-2",
        disabled: loadingOperationTypes,
        options: operationTypeOptions,
        helperText: "Choose the type of operation for this material",
      },
      {
        name: "name",
        type: "text",
        label: "Material Name",
        placeholder: "Enter material name...",
        required: true,
        className: "col-span-2",
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear, descriptive name for the material",
      },
      {
        name: "size",
        type: "text",
        label: "Size",
        placeholder: "e.g., 2 inch, 14 AWG",
        required: true,
        minLength: 1,
        helperText: "Specify the size or dimension of the material",
      },
      {
        name: "quantity",
        type: "number",
        label: "Quantity",
        placeholder: "10",
        required: true,
        min: 1,
        suffix: <Package className="h-4 w-4 text-muted-foreground" />,
        helperText: "Number of units required",
      },
      {
        name: "costPerUnit",
        type: "decimal",
        label: "Cost per Unit ($)",
        placeholder: "15.50",
        required: true,
        min: 0,
        step: 0.01,
        prefix: <DollarSign className="h-4 w-4 text-muted-foreground" />,
        helperText: "Cost per individual unit in dollars",
      },
    ],
    [operationTypeOptions, loadingOperationTypes]
  );

  // Create form instance using FormService - following established pattern
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      operationType: "",
      size: "",
      quantity: "",
      costPerUnit: "",
    },
  });

  // Update form when editing a material - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel

    if (currentMaterial && isEditModalOpen) {
      // Find the operation type key that matches the current material's operation type
      const currentOperationType = operationTypes.find(
        (op) => op.value === currentMaterial.workOrderType
      );

      const populateData = {
        name: currentMaterial.name,
        operationType:
          currentOperationType?.key?.toString() ||
          currentMaterial.operationType?.toString(),
        size: currentMaterial.size,
        quantity: currentMaterial.quantity.toString(),
        costPerUnit: currentMaterial.costPerUnit.toString(),
      };

      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const defaultValues = {
        name: "",
        operationType: "",
        size: "",
        quantity: "",
        costPerUnit: "",
      };
      FormService.populateForm(form, defaultValues, formFields);
    }
  }, [
    currentMaterial,
    isEditModalOpen,
    isAddModalOpen,
    form,
    formFields,
    operationTypes,
    isCanceling,
  ]);

  // Handle field changes - simplified like other components
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Prevent any field changes during cancel
      if (isCanceling) return;
    },
    [isCanceling]
  );

  // Handle form submission
  const handleSubmit = async (data: MaterialFormData) => {
    if (isCanceling) return; // Prevent submit during cancel

    try {
      if (currentMaterial) {
        const updatePayload = {
          code: currentMaterial.id,
          name: data.name.trim(),
          operationType: parseInt(data.operationType),
          size: data.size.trim(),
          quantity: parseInt(data.quantity),
          costPerUnit: parseFloat(data.costPerUnit),
        };
        // Update existing material
        await updateMaterialMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          name: data.name.trim(),
          operationType: parseInt(data.operationType),
          size: data.size.trim(),
          quantity: parseInt(data.quantity),
          costPerUnit: parseFloat(data.costPerUnit),
        };
        // Create new material
        await createMaterialMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      // Reset form state - following established pattern
      FormService.resetForm(form, formFields);
      setCurrentMaterial(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Event handlers
  const handleEditClick = useCallback((material: Material) => {
    setCurrentMaterial(material);
    setIsEditModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (material: Material) => {
      try {
        await toggleStatusMutation.mutateAsync({
          code: material.id,
          isActive: !material.isActive,
          name: material.name,
          operationType: material.operationType,
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation]
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setCurrentMaterial(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setCurrentMaterial(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  return (
    <div className="">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background pt-2 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col">
          <h3 className="page-subheading">Materials</h3>
          <p className="page-subtitle">Create and manage Materials.</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createMaterialMutation.isPending || loadingOperationTypes}
        >
          {createMaterialMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Material
        </Button>
      </div>

      {/* Grid */}
      <div className="relative pb-4">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search materials..."
          className="pl-8 w-full sm:w-[250px]"
          value={searchInput}
          onChange={handleSearchChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(materials) &&
          materials.map((material) => (
            <MaterialCard
              key={material.id}
              material={material}
              onEdit={handleEditClick}
              onToggleActive={handleToggleActive}
              isUpdating={updateMaterialMutation.isPending}
              currentMaterial={currentMaterial}
              isToggling={toggleStatusMutation.isPending}
            />
          ))}

        {Array.isArray(materials) && materials.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
            isLoadingOperationTypes={loadingOperationTypes}
          />
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Material</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createMaterialMutation.isPending}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createMaterialMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={
                      createMaterialMutation.isPending || loadingOperationTypes
                    }
                  >
                    {createMaterialMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Save Material"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Material</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateMaterialMutation.isPending}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateMaterialMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={
                      updateMaterialMutation.isPending || loadingOperationTypes
                    }
                  >
                    {updateMaterialMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Material"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialsTab;
